import { type Site, sites } from '@labeling/domain';
import { Button, Combobox, Modal, TextField } from '@lego/connect-components-react';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import type { TableColumn } from '@/components/table';
import { TableSkeleton } from '@/components/table-skeleton';
import { Table } from '../../components';
import { useApi } from '../../providers/api';
import { useToastContext } from '../../providers/toast';
import { useUserStore } from '../../stores/user-store';
import type { LocationData } from '../../types/types';
import { fetchAllPages } from '../../utils/pagination';
import { canCreateLocation, canDeleteLocation } from '../../utils/permissions';
import { assertResponseOk, toErrorMessage } from '../../utils/response';

// `air` only has print triggers in the integration Loftware tenant.
const devOnlySites: Site[] = ['air'];
const integrationSites: Site[] = ['kom', 'kld', 'kld_packing', ...devOnlySites];

export const LocationsPage = () => {
  const { t } = useTranslation();
  const { client } = useApi();
  const { user, isAdminOrMaintainer } = useUserStore();
  const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isNewLocation, setIsNewLocation] = useState(false);
  const triggerRef = useRef<HTMLElement>(null);
  const { showToast } = useToastContext();
  const [editForm, setEditForm] = useState({
    site: 'kom' as Site,
    area: '',
    description: '',
  });
  const [portalTarget, setPortalTarget] = useState<HTMLDivElement | null>(null);

  const {
    data: locations,
    isLoading: locationsIsLoading,
    mutate: reloadLocations,
  } = useSWR('locations', () =>
    fetchAllPages((query) => client.v1.locations.$get({ query }).then((r) => r.json()))
  );

  const canEdit = isAdminOrMaintainer();
  const canCreate = canCreateLocation(user);
  const canDelete = canDeleteLocation(user);

  const getSiteOptions = () => {
    let availableSites: readonly Site[] = sites;
    if (import.meta.env.MODE === 'dev') availableSites = integrationSites;
    if (import.meta.env.MODE === 'prod') {
      availableSites = sites.filter((site) => !devOnlySites.includes(site));
    }
    return availableSites.map((site) => ({
      value: site,
      label: site.toUpperCase(),
    }));
  };

  const handleLocationClick = (location: LocationData) => {
    triggerRef.current = document.activeElement as HTMLElement;
    setSelectedLocation(location);
    setIsNewLocation(false);
    setEditForm({
      site: location.site,
      area: location.area || '',
      description: location.description || '',
    });
    setIsDialogOpen(true);
  };

  const handleAddLocation = () => {
    triggerRef.current = document.activeElement as HTMLElement;
    setSelectedLocation(null);
    setIsNewLocation(true);
    setEditForm({
      site: 'kom',
      area: '',
      description: '',
    });
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => setIsDialogOpen(false);

  const handleSave = async () => {
    if (!canEdit) return;

    try {
      if (isNewLocation) {
        const response = await client.v1.locations.$post({
          json: {
            site: editForm.site,
            area: editForm.area,
            description: editForm.description,
          },
        });
        await assertResponseOk(response, t('locationsPage.response.locationCreateFailed'));
        showToast(t('locationsPage.response.locationCreatedSuccess'), 'success');
      } else if (selectedLocation) {
        const response = await client.v1.locations[':id'].$put({
          param: { id: selectedLocation.id.toString() },
          json: {
            site: editForm.site,
            area: editForm.area,
            description: editForm.description,
          },
        });
        await assertResponseOk(response, t('locationsPage.response.locationUpdateFailed'));
        showToast(t('locationsPage.response.locationUpdatedSuccess'), 'success');
      }

      await reloadLocations();
      handleCloseDialog();
    } catch (error) {
      showToast(toErrorMessage(error, t('locationsPage.response.locationError')), 'error');
    }
  };

  const handleDelete = async () => {
    if (!selectedLocation || !canDelete) return;

    try {
      const response = await client.v1.locations[':id'].$delete({
        param: { id: selectedLocation.id.toString() },
      });
      await assertResponseOk(response, t('locationsPage.response.locationDeleteFailed'));
      await reloadLocations();
      showToast(t('locationsPage.response.locationDeletedSuccess'), 'success');
      handleCloseDialog();
    } catch (error) {
      showToast(toErrorMessage(error, t('locationsPage.response.locationDeleteFailed')), 'error');
    }
  };

  const tableColumns: TableColumn<LocationData>[] = useMemo(
    () => [
      {
        key: 'site',
        label: t('locationsPage.display.site'),
        sortable: true,
        render: (value) => <span className="text-core-slate-1300 font-medium">{value}</span>,
      },
      {
        key: 'area',
        label: t('locationsPage.display.area'),
        sortable: true,
        accessor: (location: LocationData) => location.area || '-',
      },
      {
        key: 'description',
        label: t('locationsPage.display.description'),
        sortable: true,
        accessor: (location: LocationData) => location.description || '-',
      },
      {
        key: 'updatedAt',
        label: t('locationsPage.display.lastUpdated'),
        sortable: true,
        accessor: (location: LocationData) =>
          new Date(location.updatedAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          }),
      },
    ],
    [t]
  );

  const isLoading = locationsIsLoading;

  if (!locations || isLoading) {
    return <TableSkeleton columns={tableColumns.length} page showAction />;
  }

  return (
    <>
      <div className="px-400 py-400">
        <div className="flex items-start justify-between mb-300">
          <h1 className="text-heading-md font-brand font-bold text-core-slate-1300">
            {`${t('locationsPage.display.locationsTitle')} (${locations.itemCount})`}
          </h1>
          {canCreate && (
            <Button
              variant="primary"
              size="small"
              className="ml-200 text-xs font-semibold leading-tight px-200 py-75 h-auto min-h-0"
              onClick={handleAddLocation}
            >
              {t('locationsPage.action.addLocation')}
            </Button>
          )}
        </div>
        <Table
          data={locations.items}
          columns={tableColumns}
          onRowClick={handleLocationClick}
          emptyMessage={t('locationsPage.display.noLocationsFound')}
          searchPlaceholder={t('locationsPage.display.searchLocationsPlaceholder')}
          ariaLabel={t('locationsPage.display.locationsTitle')}
        />
      </div>

      <Modal
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        triggerRef={triggerRef}
        size="medium"
      >
        <Modal.Title>
          {isNewLocation
            ? t('locationsPage.action.addNewLocation')
            : canEdit
              ? t('locationsPage.action.editLocationTitle', {
                  site: selectedLocation?.site,
                  area: selectedLocation?.area,
                })
              : t('locationsPage.action.viewLocationTitle', {
                  site: selectedLocation?.site,
                  area: selectedLocation?.area,
                })}
        </Modal.Title>
        <Modal.Content>
          <div ref={setPortalTarget} className="grid grid-cols-1 gap-200 text-body-xs">
            <div>
              <span className="font-brand font-bold text-core-slate-1100 block mb-50">
                {t('locationsPage.display.siteLabel')}
              </span>
              <Combobox
                fullWidth
                value={editForm.site ? [editForm.site] : []}
                onChange={(_e, items) =>
                  setEditForm({
                    ...editForm,
                    site: (items[0]?.value ?? '') as (typeof sites)[number],
                  })
                }
                placeholder={t('locationsPage.display.selectSite')}
                size="medium"
                disabled={!canEdit}
                {...(portalTarget ? { portalTarget } : {})}
              >
                {getSiteOptions().map((o) => (
                  <Combobox.Option key={o.value} value={o.value}>
                    {o.label}
                  </Combobox.Option>
                ))}
              </Combobox>
            </div>

            <div>
              <span className="font-brand font-bold text-core-slate-1100 block mb-50">
                {t('locationsPage.display.areaLabel')}
              </span>
              <TextField
                value={editForm.area}
                onChange={(_e, value) => setEditForm({ ...editForm, area: value })}
                placeholder={t('locationsPage.display.enterArea')}
                size="medium"
                disabled={!canEdit}
              />
            </div>

            <div>
              <span className="font-brand font-bold text-core-slate-1100 block mb-50">
                {t('locationsPage.display.descriptionLabel')}
              </span>
              <TextField
                value={editForm.description}
                onChange={(_e, value) => setEditForm({ ...editForm, description: value })}
                placeholder={t('locationsPage.display.enterDescription')}
                size="medium"
                disabled={!canEdit}
              />
            </div>

            {!isNewLocation && selectedLocation && (
              <>
                <div>
                  <span className="font-brand font-bold text-core-slate-1100">ID:</span>
                  <p className="text-core-slate-1300">{selectedLocation.id}</p>
                </div>

                <div>
                  <span className="font-brand font-bold text-core-slate-1100">
                    {t('locationsPage.display.createdLabel')}
                  </span>
                  <p className="text-core-slate-1300">
                    {new Date(selectedLocation.createdAt).toLocaleString()}
                  </p>
                </div>

                <div>
                  <span className="font-brand font-bold text-core-slate-1100">
                    {t('locationsPage.display.lastUpdatedLabel')}
                  </span>
                  <p className="text-core-slate-1300">
                    {new Date(selectedLocation.updatedAt).toLocaleString()}
                  </p>
                </div>
              </>
            )}
          </div>
        </Modal.Content>
        <Modal.Footer>
          <Button variant="neutral" size="small" onClick={handleCloseDialog}>
            {canEdit ? t('locationsPage.action.cancel') : t('locationsPage.action.close')}
          </Button>
          {canEdit && (
            <>
              {!isNewLocation && canDelete && (
                <Button variant="negative" size="small" onClick={handleDelete}>
                  {t('locationsPage.action.delete')}
                </Button>
              )}
              <Button variant="primary" size="small" onClick={handleSave}>
                {isNewLocation
                  ? t('locationsPage.action.createLocation')
                  : t('locationsPage.action.saveChanges')}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};
